import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { 
  Typography, 
  Button, 
  Box, 
  Grid, 
  Paper, 
  Chip,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  TextField,
  IconButton,
  Divider,
  CircularProgress
} from "@mui/material";
import { 
  PlayArrow as PlayArrowIcon, 
  Schedule as ScheduleIcon, 
  ContentCopy as ContentCopyIcon, 
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon
} from "@mui/icons-material";
import { 
  fetchAutomationById, 
  updateAutomation, 
  deleteAutomation, 
  runAutomation 
} from "../redux/thunks/automationsThunks";
import StepsList from "../components/automation/StepsList";
import StepEditor from "../components/automation/StepEditor";
import CodeEditor from "../components/automation/CodeEditor";
import RunHistory from "../components/automation/RunHistory";

const AutomationDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [automation, setAutomation] = useState(null);
  // For debugging steps issue
  const [originalStepsData, setOriginalStepsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStep, setSelectedStep] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [generatedCode, setGeneratedCode] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAutomationDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        
        try {
          const result = await dispatch(fetchAutomationById(id));
          const automationData = result;
          
          if (!automationData) {
            throw new Error('Failed to fetch automation data');
          }
          
          console.log('Fetched automation data:', automationData);
          console.log('Steps data:', automationData.steps);
          
          // Initialize steps as empty array if missing
          // Save original steps data for debugging
          setOriginalStepsData(automationData.steps);
          console.log('Steps data type:', typeof automationData.steps);
          console.log('Is steps an array?', Array.isArray(automationData.steps));
          
          if (!automationData.steps) {
            console.warn('Steps property is missing, initializing as empty array');
            automationData.steps = [];
          } else if (!Array.isArray(automationData.steps)) {
            console.warn('Steps property is not an array, converting to array');
            try {
              console.log('Attempting to convert steps to array, keys:', Object.keys(automationData.steps || {}));
              automationData.steps = Object.values(automationData.steps || {});
              console.log('After conversion:', automationData.steps);
            } catch (error) {
              console.error('Error converting steps to array:', error);
              automationData.steps = [];
            }
          }
          setAutomation(automationData);
          setEditedName(automationData.name);
          setEditedDescription(automationData.description || '');
          
          // Only generate code if steps exist
          if (automationData.steps && automationData.steps.length > 0) {
            setGeneratedCode(generateWebDriverCode(automationData.steps));
          } else {
            console.warn('No steps found for this automation');
            setGeneratedCode('// No steps found for this automation');
          }
        } catch (apiError) {
          console.error("API call failed:", apiError);
          setError(`Failed to load automation: ${apiError.message || 'Unknown error'}`);
          
          // Initialize with empty data instead of mock data
          setAutomation({
            id: parseInt(id),
            name: '',
            description: '',
            steps: [],
            tags: [],
            runHistory: []
          });
          
          setEditedName('');
          setEditedDescription('');
          setGeneratedCode('// Error loading automation steps');
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching automation:", error);
        setError("Failed to load automation details");
        setLoading(false);
      }
    };

    fetchAutomationDetail();
  }, [dispatch, id]);

  const generateWebDriverCode = (steps) => {
    let code = `// WebDriver TypeScript code for automation\nimport { Builder, By, Key, until } from 'selenium-webdriver';\n\n(async function example() {\n  let driver = await new Builder().forBrowser('chrome').build();\n  try {\n`;

    if (!Array.isArray(steps)) {
      console.error("generateWebDriverCode: steps is not an array", steps);
      steps = []; // Avoid crashing if steps is not an array
    }

    steps.forEach(step => {
      // Ensure step exists
      if (!step) {
        code += `    // Invalid step data: step is null or undefined\n`;
        return; // Skip this iteration
      }

      const inputData = step.inputData || {}; // Default to empty object if inputData is missing

      switch (step.type) {
        case 'navigate':
          const url = step.url || inputData.url;
          if (url) {
            code += `    await driver.get("${url}");\n`;
          } else {
            code += `    // Navigate: URL is missing in step data\n`;
          }
          break;
        case 'click':
          if (inputData.selector) {
            code += `    await driver.wait(until.elementLocated(By.css("${inputData.selector}")), 5000);\n`;
            code += `    await driver.findElement(By.css("${inputData.selector}")).click();\n`;
          } else {
            code += `    // Click: Selector is missing in inputData\n`;
          }
          break;
        case 'type': // Assumes inputData.value contains the text to type
          if (inputData.selector && typeof inputData.value !== 'undefined') {
            code += `    await driver.wait(until.elementLocated(By.css("${inputData.selector}")), 5000);\n`;
            code += `    await driver.findElement(By.css("${inputData.selector}")).sendKeys("${inputData.value}");\n`;
          } else if (!inputData.selector) {
            code += `    // Type: Selector is missing in inputData\n`;
          } else {
            code += `    // Type: Value to type is missing in inputData\n`;
          }
          break;
        case 'wait': // Assumes inputData.value contains the duration
          if (typeof inputData.value !== 'undefined') {
            code += `    await driver.sleep(${inputData.value});\n`;
          } else {
            code += `    // Wait: Duration (value) is missing in inputData\n`;
          }
          break;
        default:
          code += `    // Unsupported step type: ${step.type}\n`;
      }
    });

    code += `  } finally {\n    if (driver) { // Ensure driver exists before quitting\n      await driver.quit();\n    }\n  }\n})();`;
    return code;
  };

  const handleStepSelect = (step) => {
    setSelectedStep(step);
  };

  const handleStepSave = async (editedStep) => {
    const updatedSteps = automation.steps.map(step => 
      step.id === editedStep.id ? editedStep : step
    );
    
    setAutomation({
      ...automation,
      steps: updatedSteps
    });
    
    setGeneratedCode(generateWebDriverCode(updatedSteps));
    setSelectedStep(null);
    
    try {
      await dispatch(updateAutomation(automation.id, {
        ...automation,
        steps: updatedSteps
      }));
    } catch (error) {
      console.error("Error updating automation steps:", error);
    }
  };

  const handleStepCancel = () => {
    setSelectedStep(null);
  };

  const handleStepReorder = async (sourceIndex, destinationIndex) => {
    const reorderedSteps = [...automation.steps];
    const [removed] = reorderedSteps.splice(sourceIndex, 1);
    reorderedSteps.splice(destinationIndex, 0, removed);
    
    setAutomation({
      ...automation,
      steps: reorderedSteps
    });
    
    setGeneratedCode(generateWebDriverCode(reorderedSteps));
    
    try {
      await dispatch(updateAutomation(automation.id, {
        ...automation,
        steps: reorderedSteps
      }));
    } catch (error) {
      console.error("Error reordering steps:", error);
    }
  };

  const handleStepDelete = async (stepId) => {
    const updatedSteps = automation.steps.filter(step => step.id !== stepId);
    
    setAutomation({
      ...automation,
      steps: updatedSteps
    });
    
    setGeneratedCode(generateWebDriverCode(updatedSteps));
    
    if (selectedStep && selectedStep.id === stepId) {
      setSelectedStep(null);
    }
    
    try {
      await dispatch(updateAutomation(automation.id, {
        ...automation,
        steps: updatedSteps
      }));
    } catch (error) {
      console.error("Error deleting step:", error);
    }
  };

  const handleAddStep = async () => {
    const newStep = {
      id: Math.max(...automation.steps.map(s => s.id), 0) + 1,
      action: "click",
      selector: "",
      value: ""
    };
    
    setAutomation({
      ...automation,
      steps: [...automation.steps, newStep]
    });
    
    setSelectedStep(newStep);
    
    try {
      await dispatch(updateAutomation(automation.id, {
        ...automation,
        steps: [...automation.steps, newStep]
      }));
    } catch (error) {
      console.error("Error adding step:", error);
    }
  };

  const handleToggleEdit = async () => {
    if (isEditing) {
      try {
        const updatedAutomation = {
          ...automation,
          name: editedName,
          description: editedDescription
        };
        
        setAutomation(updatedAutomation);
        
        await dispatch(updateAutomation(automation.id, updatedAutomation));
      } catch (error) {
        console.error("Error updating automation:", error);
      }
    }
    setIsEditing(!isEditing);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCodeChange = (newCode) => {
    setGeneratedCode(newCode);
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    
    switch (status.toLowerCase()) {
      case "success":
        return "success";
      case "failed":
        return "error";
      case "running":
        return "info";
      case "draft":
        return "default";
      case "inactive":
        return "warning";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!automation) {
    return (
      <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" variant="h5" gutterBottom>
          Automation not found
        </Typography>
        <Button 
          component={RouterLink} 
          to="/" 
          variant="contained" 
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Paper>
    );
  }

  return (
    <div>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link component={RouterLink} color="inherit" to="/">
          Dashboard
        </Link>
        {automation.folder && (
          <Link component={RouterLink} color="inherit" to={`/folders/${automation.folder.toLowerCase()}`}>
            {automation.folder}
          </Link>
        )}
        <Typography color="text.primary">{automation.name}</Typography>
      </Breadcrumbs>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          {isEditing ? (
            <TextField
              fullWidth
              variant="outlined"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              sx={{ mr: 2 }}
            />
          ) : (
            <Typography variant="h4">{automation.name}</Typography>
          )}
          
          <Box>
            {automation.tags && automation.tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" sx={{ mr: 1 }} />
            ))}
            <IconButton size="small" onClick={handleToggleEdit}>
              {isEditing ? <SaveIcon /> : <EditIcon />}
            </IconButton>
          </Box>
        </Box>
        
        {isEditing ? (
          <TextField
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={editedDescription}
            onChange={(e) => setEditedDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
        ) : (
          <Typography variant="body1" paragraph>
            {automation.description}
          </Typography>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<PlayArrowIcon />}
              sx={{ mr: 1 }}
              onClick={() => {
                console.log('Running automation:', automation.id);
                dispatch(runAutomation(automation.id));
              }}
            >
              Run Now
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<ScheduleIcon />}
              sx={{ mr: 1 }}
            >
              Schedule
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<ContentCopyIcon />}
              sx={{ mr: 1 }}
            >
              Duplicate
            </Button>
            <Button 
              variant="outlined" 
              color="error" 
              startIcon={<DeleteIcon />}
              onClick={() => {
                if (window.confirm("Are you sure you want to delete this automation?")) {
                  dispatch(deleteAutomation(automation.id));
                  window.location.href = "/";
                }
              }}
            >
              Delete
            </Button>
          </Box>
          <Box>
            <Chip 
              label={`Status: ${automation.status}`} 
              color={getStatusColor(automation.status)} 
            />
          </Box>
        </Box>
      </Paper>
      
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Steps" />
        <Tab label="Code" />
        <Tab label="Variables" />
        <Tab label="Run History" />
      </Tabs>
      
      {/* Debug info for steps */}
      {originalStepsData !== null && (
        <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
          <Typography variant="subtitle2">Debug Info:</Typography>
          <Typography variant="body2">
            Steps data type: {typeof originalStepsData}<br />
            Is array: {Array.isArray(originalStepsData).toString()}<br />
            Raw data: {JSON.stringify(originalStepsData).substring(0, 100)}...
          </Typography>
        </Paper>
      )}
      
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <StepsList 
              steps={automation.steps} 
              onStepSelect={handleStepSelect}
              onStepReorder={handleStepReorder}
              onStepDelete={handleStepDelete}
            />
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={handleAddStep}
              fullWidth
              sx={{ mt: 2 }}
            >
              Add Step
            </Button>
          </Grid>
          <Grid item xs={12} md={8}>
            {selectedStep ? (
              <StepEditor 
                step={selectedStep} 
                onSave={handleStepSave} 
                onCancel={handleStepCancel} 
              />
            ) : (
              <Paper elevation={2} sx={{ p: 3, height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Typography variant="body1" color="text.secondary">
                  Select a step to edit or add a new step
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}
      
      {activeTab === 1 && (
        <CodeEditor code={generatedCode} onChange={handleCodeChange} />
      )}
      
      {activeTab === 2 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Variables &amp; Credentials
          </Typography>
          <Grid container spacing={3}>
            {automation.variables.map((variable) => (
              <Grid item xs={12} sm={6} md={4} key={variable.name}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="subtitle1">{variable.name}</Typography>
                  <Typography variant="body2">{variable.value}</Typography>
                </Paper>
              </Grid>
            ))}
            <Grid item xs={12} sm={6} md={4}>
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  height: "100%", 
                  display: "flex", 
                  justifyContent: "center", 
                  alignItems: "center",
                  border: "1px dashed rgba(0, 0, 0, 0.12)",
                  cursor: "pointer"
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <AddIcon color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Add Variable
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      )}
      
      {activeTab === 3 && (
        <RunHistory runs={automation.runHistory} />
      )}
    </div>
  );
};

export default AutomationDetail;
